import fs from 'node:fs'
import path from 'node:path'
import { fileURLToPath } from 'node:url'
import type { CommandOptions } from '@cli-types/core-engine/commands.js'
import type { CliPackageJson } from '@cli-types/core-engine/utils.js'
import type { Command } from 'commander'
import { CLI_PACKAGE_NAME } from '@cli-constants/cli.js'
import { BETTERSTART_DIR } from '@cli-constants/paths.js'
import { findUpwards } from '@cli-utils/files.js'

export function betterstartDir(cwd: string, ...segments: string[]): string {
  return path.join(cwd, BETTERSTART_DIR, ...segments)
}

export function toPosixPath(value: string): string {
  return value.replace(/\\/g, '/')
}

export function trimDotSlash(value: string): string {
  return value.replace(/^[.][/\\]/, '')
}

export function normalizePosixDir(value: string): string {
  return trimDotSlash(toPosixPath(value)).replace(/\/+$/, '')
}

export function toProjectAbsolutePath(cwd: string, filePath: string): string {
  return path.isAbsolute(filePath) ? filePath : path.join(cwd, ...filePath.split('/'))
}

export function readLocalProjectFile(cwd: string, filePath: string): string | null {
  const fullPath = toProjectAbsolutePath(cwd, filePath)
  return fs.existsSync(fullPath) ? fs.readFileSync(fullPath, 'utf-8') : null
}

export function isInsideDir(child: string, parent: string): boolean {
  const relative = path.relative(parent, child)
  return relative === '' || (!relative.startsWith('..') && !path.isAbsolute(relative))
}

export function removePath(cwd: string, filePath: string): boolean {
  const fullPath = toProjectAbsolutePath(cwd, filePath)
  const existed = fs.existsSync(fullPath)
  fs.rmSync(fullPath, { recursive: true, force: true })
  return existed
}

export function cleanupEmptyDirs(
  cwd: string,
  deletedPaths: string[],
  stopRoots: Iterable<string>
): void {
  const projectRoot = path.resolve(cwd)
  const stops = new Set<string>([projectRoot, ...stopRoots])

  for (const deletedPath of deletedPaths) {
    let currentDir = path.dirname(toProjectAbsolutePath(cwd, deletedPath))

    while (isInsideDir(currentDir, projectRoot) && !stops.has(currentDir)) {
      if (!fs.existsSync(currentDir)) {
        currentDir = path.dirname(currentDir)
        continue
      }

      if (fs.readdirSync(currentDir).length > 0) {
        break
      }

      fs.rmdirSync(currentDir)
      currentDir = path.dirname(currentDir)
    }
  }
}

function findCliPackage(): { dir: string; pkg: CliPackageJson } | null {
  const start = path.dirname(fileURLToPath(import.meta.url))

  return (
    findUpwards(start, (dir) => {
      const pkgPath = path.join(dir, 'package.json')
      if (!fs.existsSync(pkgPath)) return undefined

      try {
        const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as CliPackageJson
        return pkg.name === CLI_PACKAGE_NAME ? { dir, pkg } : undefined
      } catch {
        return undefined
      }
    }) ?? null
  )
}

export function findCliRoot(): string {
  const found = findCliPackage()
  if (found) {
    return found.dir
  }

  // Fallback: assume 2 levels up from dist/
  return path.resolve(path.dirname(fileURLToPath(import.meta.url)), '..', '..')
}

export function getCurrentCliVersion(): string {
  return findCliPackage()?.pkg.version ?? '0.0.0'
}

export function resolveCliAssetPath(...segments: string[]): string {
  const root = findCliRoot()
  const builtPath = path.join(root, 'dist', 'assets', ...segments)
  if (fs.existsSync(builtPath)) {
    return builtPath
  }

  return path.join(root, ...segments)
}

export function resolveCommandCwd(command: Command): string {
  const options = command.opts<CommandOptions>()
  return typeof options.cwd === 'string' ? path.resolve(options.cwd) : process.cwd()
}
